/**
 * Template-picker modal for "+ Skapa avtal".
 *
 * Renders the available template versions and, on selection, creates a
 * draft contract via `contracts.create_from_template` and navigates the
 * caller to the editor. Group routing piggybacks on the active-group
 * header (same convention as TemplatesPage).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTemplateVersion } from '@hyranai/regta-templates';
import { tenantApi, type DocumentEngine, type TemplateListRow } from '../api.js';
import { LegacyCreateLink, contractEditorPath, defaultEngine } from './DocumentEngine.js';
import { useTenantSession } from '../auth-context.js';
import { useActiveGroup } from './GroupPicker.js';
import { TemplateFavoriteStar, sortFavoritesFirst } from './TemplateFavoriteStar.js';
import { useInvalidate, useTenantQuery } from '../query.js';

export function CreateContractModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { token } = useTenantSession();
  const { activeGroupId } = useActiveGroup();
  const invalidate = useInvalidate();

  const { data, error, isLoading } = useTenantQuery(
    'templates.list',
    () => tenantApi.templates.list(),
  );
  // Connected-only templates (clean tilläggsavtal frames, ADR-0061) are
  // hidden from the standalone picker — they're created from a parent
  // contract's "Kopplade avtal" panel. Mirrors TemplatesPage's filter; the
  // backend also refuses them standalone (template_is_connected_only).
  const templates = useMemo(() => {
    if (!data?.templates) return null;
    const rows = data.templates.filter((t) => {
      const mod = getTemplateVersion(t.template_key, t.version);
      return !mod?.metadata.connectedOnly;
    });
    return sortFavoritesFirst(rows);
  }, [data]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [writeErr, setWriteErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!templates) return null;
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      `${t.name} ${t.description ?? ''} ${t.category}`
        .toLowerCase()
        .includes(q),
    );
  }, [templates, query]);

  const create = async (t: TemplateListRow, engine: DocumentEngine = defaultEngine(t)) => {
    if (!token) return;
    if (!activeGroupId) {
      setWriteErr('Välj en grupp innan du skapar avtalet.');
      return;
    }
    setBusyId(t.version_id);
    setWriteErr(null);
    try {
      const r = await tenantApi.contracts.create_from_template({
        template_version_id: t.version_id,
        ...(engine === 'ir' ? { engine } : {}),
      });
      await invalidate('contracts.list');
      onClose();
      navigate(contractEditorPath(r.contract_id, engine));
    } catch (e) {
      setWriteErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-xl flex-col gap-3 rounded bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Välj mall för det nya avtalet</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-stone-500 hover:text-stone-800"
          >
            Stäng
          </button>
        </div>

        {(writeErr || error) && (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            {writeErr ?? (error as Error).message}
          </div>
        )}

        {templates && templates.length > 0 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Sök bland mallar…"
            className="w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm placeholder-stone-400 focus:border-stone-500 focus:outline-none"
          />
        )}

        {isLoading || filtered === null ? (
          <div className="text-sm text-stone-400">Laddar mallar…</div>
        ) : templates && templates.length === 0 ? (
          <div className="text-sm text-stone-500">
            Inga mallar är tillgängliga ännu.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-stone-500">
            Inga mallar matchar din sökning.
          </div>
        ) : (
          <ul className="max-h-[60vh] divide-y overflow-y-auto rounded border">
            {filtered.map((t) => (
              <li
                key={t.version_id}
                className="flex items-center justify-between gap-3 p-3 hover:bg-stone-50"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <TemplateFavoriteStar
                    templateId={t.template_id}
                    favorited={t.favorited}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{t.name}</div>
                    <div className="flex items-center gap-1.5 text-xs text-stone-500">
                      {t.category} · {t.version}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <button
                    type="button"
                    onClick={() => void create(t)}
                    disabled={busyId === t.version_id || !activeGroupId}
                    className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  >
                    {busyId === t.version_id ? 'Skapar…' : 'Skapa'}
                  </button>
                  {t.ir_available && (
                    <LegacyCreateLink
                      onClick={() => void create(t, 'legacy')}
                      disabled={busyId === t.version_id || !activeGroupId}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
